/**
 * What "View Post Content" has to leave on screen.
 *
 * #1924: the item page wired the button to a handler that only set the active
 * tab to Post Content. On mobile the button lives inside the Edit Content
 * drawer (a right-positioned `q-dialog`), which stays open on top of whatever
 * tab is behind it. When Post Content was already the active tab, selecting it
 * again changed nothing and the drawer kept covering the screen, so the button
 * looked broken.
 *
 * The button's promise is a destination, not a toggle: from any starting state
 * it must end with Post Content selected AND the drawer out of the way. That is
 * the whole decision, kept here so it can be pinned by a test — the page itself
 * cannot be mounted in this repo's vitest setup.
 */
export const POST_CONTENT_TAB = 'context' as const;

export type PostContentTabId = typeof POST_CONTENT_TAB;

export interface ViewPostContentState {
  /** The unified tab currently selected on the item page. */
  activeTab: string;
  /** Whether the mobile Edit Content drawer is open over it. */
  isDraftDrawerOpen: boolean;
}

export interface ViewPostContentTarget {
  activeTab: PostContentTabId;
  isDraftDrawerOpen: boolean;
}

/**
 * The state the page must be in after "View Post Content" is tapped.
 *
 * Intentionally ignores the current state: there is no case where tapping this
 * button should leave the drawer open, including the already-on-Post-Content
 * case that #1924 reported.
 */
export function viewPostContentTarget(_current: ViewPostContentState): ViewPostContentTarget {
  return { activeTab: POST_CONTENT_TAB, isDraftDrawerOpen: false };
}
